import { isExecutableAskResult, askResultToQuerySpec, type AskResponse } from '@console/types'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAskMutation } from '../ask/queries.js'
import { encodeQuerySpec } from '../ask/result-id.js'
import {
  describeFilter,
  runsUrlWouldLoseMeaning,
  toConsoleRunFilter,
} from '../ask/filter-mapping.js'
import { CannotAnswerRenderer, RouteToViewRenderer } from '../ask/renderers.js'
import { searchParamsForView } from '../runs/url.js'
import { Button, HairlineRule, Input } from '../ui/index.js'
import styles from './AskPage.module.css'

/**
 * Ask — natural language in, a query specification out.
 *
 * The six example prompts (spec §8) deliberately cover different result
 * shapes and end with one that Ask *refuses*. A tool that shows its limits
 * earns trust; operators arriving from other LLM chatbots have been trained
 * to expect an approximate answer to anything, and demonstrating the refusal
 * on first contact is what recalibrates that.
 */
const EXAMPLE_PROMPTS = [
  'Refused runs last week',
  'Total AED value of quotes waiting for my approval',
  'Median unit price for Al Noor',
  'Which customers most often need my approval?',
  'Quote volume per week',
  // The teaching example. Deliberately a question Ask does not answer.
  'Why did autonomy drop last week?',
]

export function AskPage() {
  const navigate = useNavigate()
  const mutation = useAskMutation()
  const [prompt, setPrompt] = useState('')
  const [response, setResponse] = useState<AskResponse | null>(null)

  function submit(text: string): void {
    if (text.trim() === '') return
    setResponse(null)
    mutation.mutate(text, {
      onSuccess: (result) => {
        route(result)
      },
    })
  }

  /**
   * Where a classification goes (spec §7). Executable results navigate to
   * their own bookmarkable URL, which is where the query actually runs —
   * classification and execution stay separate requests.
   */
  function route(askResponse: AskResponse): void {
    const { result } = askResponse

    if (result.kind === 'filter' && !runsUrlWouldLoseMeaning(result.filter)) {
      const params = searchParamsForView(toConsoleRunFilter(result.filter), 1, 'time', 'desc')
      navigate(`/runs?${params.toString()}`)
      return
    }

    if (result.kind !== 'filter' && isExecutableAskResult(result)) {
      const id = encodeQuerySpec(askResultToQuerySpec(result))
      navigate(`/ask/result/${id}?q=${encodeURIComponent(askResponse.question)}`)
      return
    }

    // `route_to_view`, `cannot_answer`, and the filter this page cannot
    // faithfully hand to the Runs page all stay here and render below.
    setResponse(askResponse)
  }

  function reset(): void {
    setResponse(null)
    setPrompt('')
  }

  return (
    <main className={styles['page']}>
      <Link to="/" className={styles['home']}>
        ← Home
      </Link>
      <h1>Ask</h1>
      <p className={styles['intro']}>
        Ask a question about your runs. I turn it into a query and show you the result — or tell you
        plainly when I can&apos;t.
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          submit(prompt)
        }}
        className={styles['form']}
      >
        <div className={styles['inputWrap']}>
          <Input
            label="What would you like to know?"
            value={prompt}
            onChange={(event) => {
              setPrompt(event.target.value)
            }}
          />
        </div>
        <Button type="submit" variant="primary" disabled={mutation.isPending}>
          {mutation.isPending ? 'Asking…' : 'Ask'}
        </Button>
      </form>

      {response === null && !mutation.isPending && (
        <div className={styles['examples']}>
          <p className={styles['label']}>Try one:</p>
          <div className={styles['exampleButtons']}>
            {EXAMPLE_PROMPTS.map((example) => (
              <Button
                key={example}
                variant="secondary"
                onClick={() => {
                  setPrompt(example)
                  submit(example)
                }}
              >
                {example}
              </Button>
            ))}
          </div>
        </div>
      )}

      {mutation.isError && (
        <p role="alert" className={styles['error']}>
          {mutation.error.message}
        </p>
      )}

      {response !== null && (
        <>
          <HairlineRule />
          <p className={styles['asked']}>
            You asked: <em>&quot;{response.question}&quot;</em>
          </p>

          {response.result.kind === 'cannot_answer' && (
            <CannotAnswerRenderer
              result={response.result}
              onSuggestionClick={(question) => {
                // Populates only. The operator confirms before anything runs
                // (spec §5.7) — that is the whole reason this doesn't submit.
                setPrompt(question)
                setResponse(null)
              }}
            />
          )}

          {response.result.kind === 'route_to_view' && (
            <RouteToViewRenderer result={response.result} />
          )}

          {response.result.kind === 'filter' && (
            /**
             * Reached only when the filter says something the Runs page
             * cannot: more than one verdict at once, a verdict this console
             * has no word for, or a rule reference it has no control for.
             * Narrowing it silently would show a list that doesn't answer
             * the question asked, so say so instead.
             */
            <div className={styles['notice']}>
              <p>
                I read that as: <strong>{describeFilter(response.result.filter)}</strong>
              </p>
              <p className={styles['label']}>
                The Runs page can&apos;t express all of that at once, so opening it would show you
                something narrower than you asked for.
              </p>
            </div>
          )}

          <Button variant="secondary" onClick={reset}>
            Ask something else
          </Button>
        </>
      )}
    </main>
  )
}
